
import componentInstanceId from '~/mixins/component-instance-id'
import createInputGroup from './input-group'

/* all the possible invalidStates (as provided by the constraint api) */
const possibleInvalidStates = [
  'badInput',
  'customError',
  'patternMismatch',
  'tooLong',
  'tooShort',
  'typeMismatch',
  'valueMissing',
  'rangeOverflow',
  'rangeUnderflow',
]

export default {
  mixins: [componentInstanceId('form-control')],

  provide() {
    return {
      formControlIds: {
        labelId: this.labelId,
        inputId: this.inputId,
        validateInputId: this.validateInputId,
      },
      formControlTriggerInput: this.onInput,
    }
  },

  props: {
    label: {
      type: String,
      default: null,
    },
    orientation: {
      type: String,
      default: 'vertical',
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    validationMessages: {
      type: Object,
      default: () => ({}),
    },
    inlineValidation: {
      type: Boolean,
      default: false,
    },
    helperText: {
      type: String,
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    justify: {
      type: String,
      default: 'start',
      validator: (val) =>
        ['start', 'end', 'space-between', 'space-around'].includes(val),
    },
  },
  data() {
    return {
      errorMessage: null,
      invalidState: null,
    }
  },
  computed: {
    customError() {
      return this.validationMessages?.customError
    },
    isInvalid() {
      return !!this.invalidState
    },
  },
  watch: {
    customError() {
      this.updateCustomError()
    },
  },

  beforeCreate() {
    this.labelId = `${this.$id}-label`
    this.inputId = `${this.$id}-input`

    /*
      Added a validate input id, because in some cases you want the input that is connected to the label separated from the input that is being validated

      For example:
      A label points to a button, but the actual validated input is a hidden text field
    */
    this.validateInputId = `${this.$id}-validate-input`
  },
  created() {
    this.inputGroup = null
  },
  mounted() {
    this.observer = new MutationObserver(this.setup)
    this.observer.observe(this.$el, { subtree: true, childList: true })
    this.setup()
  },
  destroyed() {
    this.observer.disconnect()
    this.cleanup()
  },
  methods: {
    setup() {
      const newInputRef =
        this.$el.querySelector(`#${this.validateInputId}`) ??
        this.$el.querySelector(`#${this.inputId}`)

      if (this.inputGroup?.ref() === newInputRef) {
        return
      }

      if (this.inputGroup) {
        this.cleanup()
      }

      if (newInputRef) {
        this.inputGroup = createInputGroup(newInputRef)

        /*
          Handle multiple fields grouped together. For example radio buttons with the same 'name' belong to the same group.
          An alternative is to use a fieldset to semantically group elements
        */
        if (this.isPartOfCheckboxOrRadioSet(newInputRef)) {
          this.inputGroup.add(this.collectCheckboxAndRadioElements(newInputRef))
        } else {
          const fieldset = this.getSurroundingFieldset(newInputRef)
          if (fieldset) {
            this.inputGroup.add(this.collectElementsInFieldset(fieldset))
          }
        }

        this.inputGroup.call('addEventListener', 'invalid', this.onInvalid)
        this.inputGroup.call('addEventListener', 'blur', this.onInvalid)
        this.inputGroup.call('addEventListener', 'input', this.onInput)
      } else {
        this.inputGroup = null

        console.warn('Unable to find any native form input elements!', this.$el)
      }

      this.updateCustomError()
    },

    cleanup() {
      if (this.inputGroup) {
        this.inputGroup.call('removeEventListener', 'invalid', this.onInvalid)
        this.inputGroup.call('removeEventListener', 'blur', this.onInvalid)
        this.inputGroup.call('removeEventListener', 'input', this.onInput)
      }
    },

    getSurroundingFieldset(element) {
      let par = element.parentElement
      while (par && par !== this.$el) {
        if (par.tagName === 'FIELDSET') {
          return par
        }
        par = par.parentElement
      }
      return null
    },

    isPartOfCheckboxOrRadioSet(element) {
      return element.name && ['radio', 'checkbox'].includes(element.type)
    },

    collectCheckboxAndRadioElements(element) {
      return this.$el.querySelectorAll(
        `[name=${element.name}]:not([id=${this.inputId}])`,
      )
    },

    collectElementsInFieldset(fieldset) {
      return fieldset.querySelectorAll(
        `input:not([id=${this.inputId}]),select:not([id=${this.inputId}]),textarea:not([id=${this.inputId}])`,
      )
    },

    updateCustomError() {
      if (!this.customError) {
        this.inputGroup?.callRef('setCustomValidity', '')
        this.resetValidationState()
      } else {
        this.inputGroup?.callRef('setCustomValidity', this.customError)
        /* some browsers don't support reportValidity */
        if (this.inputGroup?.prop('reportValidity')) {
          this.inputGroup?.callRef('reportValidity')
        } else {
          this.onInvalid({
            currentTarget: this.inputGroup.ref(),
          })
        }
      }
    },

    resetValidationState() {
      this.errorMessage = null
      this.invalidState = null
      this.inputGroup?.callRef('removeAttribute', 'aria-described-by')
      this.inputGroup?.callRef('removeAttribute', 'aria-invalid')
    },

    onInput() {
      if (!this.invalidState) {
        return
      }

      const allInputsAreValid = this.inputGroup.everyProp(
        'validity',
        (validity) => !validity[this.invalidState],
      )

      if (allInputsAreValid) {
        this.resetValidationState()
      }
    },

    onInvalid(e) {
      if (e.type === 'blur' && !this.inlineValidation) {
        return
      }

      e.preventDefault?.()

      // only one invalid state is currently reported
      const newInvalidState = possibleInvalidStates.find((key) => {
        return e.currentTarget.validity[key]
      })

      const newErrorMessage =
        this.validationMessages?.[newInvalidState] ||
        e.currentTarget.validationMessage

      this.invalidState = newInvalidState
      this.errorMessage = newErrorMessage

      if (this.isInvalid) {
        this.inputGroup?.callRef('setAttribute', 'aria-describedby', this.$id)
        this.inputGroup?.callRef('setAttribute', 'aria-invalid', 'true')
      }
    },
  },
}
